// EmailOTPLogin.jsx - Magic Link / Email OTP login component for VibrantCast
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { AlertCircle, Mail, ArrowLeft, Check, RefreshCw } from 'lucide-react';
import '../styles/Auth.css';

const EmailOTPLogin = () => {
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('email'); // 'email' | 'verify' | 'success'
  const [countdown, setCountdown] = useState(0);
  
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        navigate('/curator/dashboard', { replace: true });
      }
    };
    checkAuth();
  }, [navigate]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        // Check if user profile exists, create if not
        await ensureUserProfileExists(session.user);
        navigate('/curator/dashboard', { replace: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // Ensure user profile exists (for new users signing up via OTP)
  const ensureUserProfileExists = async (user) => {
    try {
      // Check if user already has a profile linked to this auth UUID
      const { data: existingUser, error: fetchError } = await supabase
        .from('users')
        .select('id')
        .eq('uuid', user.id)
        .single();
      
      if (fetchError && fetchError.code === 'PGRST116') {
        // No profile linked to this auth UUID - check if a profile exists by email
        // This handles "profile-only" users created by curators (e.g., StoryCreationPage)
        const { data: profileByEmail } = await supabase
          .from('users')
          .select('id, uuid')
          .ilike('email', user.email)
          .single();

        if (profileByEmail) {
          // Profile exists with different UUID - update it to link to auth account
          console.log('Linking existing profile to auth account:', user.email);
          await supabase
            .from('users')
            .update({ uuid: user.id })
            .eq('id', profileByEmail.id);
          
          // Assign default role if not already assigned
          const { data: existingRole } = await supabase
            .from('user_roles')
            .select('id')
            .eq('user_id', user.id)
            .single();
          
          if (!existingRole) {
            await supabase.from('user_roles').insert({
              user_id: user.id,
              role_id: 4 // Artist role
            });
          }
          
          console.log('Linked existing user profile to OTP auth:', user.email);
          return;
        }

        // No profile exists at all - create one
        const email = user.email;
        const handle = await generateUniqueHandle(email);
        const firstName = user.user_metadata?.first_name || '';
        const lastName = user.user_metadata?.last_name || '';
        
        await supabase.from('users').insert({
          uuid: user.id,
          email: email,
          handle: handle,
          first_name: firstName,
          last_name: lastName,
          display_name: firstName && lastName 
            ? `${firstName} ${lastName}`.trim() 
            : email.split('@')[0]
        });

        // Assign default role
        await supabase.from('user_roles').insert({
          user_id: user.id,
          role_id: 4 // Artist role
        });
        
        console.log('Created new user profile for OTP user:', handle);
      }
    } catch (error) {
      console.error('Error ensuring user profile:', error);
    }
  };

  // Generate unique handle from email
  const generateUniqueHandle = async (email) => {
    const baseHandle = email.split('@')[0]
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    let handle = baseHandle;
    let counter = 1;
    
    while (true) {
      const { error } = await supabase
        .from('users')
        .select('handle')
        .eq('handle', handle)
        .single();
      
      if (error && error.code === 'PGRST116') {
        break;
      }
      
      handle = `${baseHandle}-${counter}`;
      counter++;
      
      if (counter > 100) {
        handle = `${baseHandle}-${Date.now().toString(36)}`;
        break;
      }
    }
    
    return handle;
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
        }
      });

      if (error) throw error;

      setStep('verify');
      setCountdown(60); // 60 second cooldown for resend
    } catch (error) {
      console.error('OTP send error:', error);
      setError(error.message || 'Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: otpCode,
        type: 'email'
      });

      if (error) throw error;

      if (data.user) {
        setStep('success');
        // The onAuthStateChange listener will handle the redirect
      }
    } catch (error) {
      console.error('OTP verify error:', error);
      if (error.message.includes('expired')) {
        setError('Code expired. Please request a new one.');
      } else if (error.message.includes('invalid')) {
        setError('Invalid code. Please check and try again.');
      } else {
        setError(error.message || 'Failed to verify code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    
    setLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          emailRedirectTo: `${window.location.origin}/curator/dashboard`,
        }
      });

      if (error) throw error;

      setCountdown(60);
      setOtpCode('');
    } catch (error) {
      console.error('Resend OTP error:', error);
      setError(error.message || 'Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // OTP input handler - auto-advance to verify when 6 digits entered
  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtpCode(value);
  };

  // Success state
  if (step === 'success') {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="success-icon-large">
              <Check size={48} />
            </div>
            <h2>Welcome!</h2>
            <p className="auth-subtitle">
              You're signed in. Redirecting to your dashboard...
            </p>
          </div>
          <div className="loading-spinner-container">
            <div className="loading-spinner-large"></div>
          </div>
        </div>
      </div>
    );
  }

  // Verify OTP step
  if (step === 'verify') {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="otp-icon-container">
              <Mail size={32} />
            </div>
            <h2>Check Your Email</h2>
            <p className="auth-subtitle">
              We sent a verification code to<br />
              <strong>{email}</strong>
            </p>
          </div>

          {error && (
            <div className="auth-error">
              <AlertCircle size={20} />
              <div>
                <div className="error-title">Verification Failed</div>
                <p className="error-message">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleVerifyOTP} className="auth-form">
            <div className="form-group">
              <label htmlFor="otpCode">Enter 6-digit code</label>
              <div className="input-container">
                <input
                  id="otpCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={otpCode}
                  onChange={handleOtpChange}
                  placeholder="000000"
                  required
                  disabled={loading}
                  autoFocus
                  className="otp-input"
                  maxLength={6}
                />
              </div>
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={loading || otpCode.length !== 6}
            >
              {loading ? "Verifying..." : "Verify & Sign In"}
            </button>
          </form>

          <div className="otp-actions">
            <button
              onClick={handleResendOTP}
              disabled={countdown > 0 || loading}
              className="resend-button"
            >
              <RefreshCw size={16} />
              {countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}
            </button>
            
            <button
              onClick={() => {
                setStep('email');
                setOtpCode('');
                setError('');
              }}
              className="change-email-button"
            >
              Use different email
            </button>
          </div>

          <div className="auth-divider">
            <span>or</span>
          </div>

          <div className="auth-footer">
            <Link to="/login" className="back-link">
              <ArrowLeft size={16} />
              Sign in with password instead
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Email entry step
  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo-container">
            <img 
              src="https://res.cloudinary.com/dh9jfogh1/image/upload/t_fitmax-300/v1729693346/vibrant-logo_ds6pwg.png" 
              alt="VibrantCast" 
              className="auth-logo-inline"
            />
          </div>
          <h2>Sign In with Email</h2>
          <p className="auth-subtitle">
            No password needed — we'll send you a verification code
          </p>
        </div>

        {error && (
          <div className="auth-error">
            <AlertCircle size={20} />
            <div>
              <div className="error-title">Error</div>
              <p className="error-message">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSendOTP} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div className="input-container">
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                disabled={loading}
                autoFocus
              />
            </div>
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading || !email}
          >
            {loading ? "Sending..." : "Send Verification Code"}
          </button>
        </form>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <div className="auth-footer">
          <div className="footer-links">
            <Link to="/login" className="back-link">
              <ArrowLeft size={16} />
              Sign in with password
            </Link>
            <div className="signup-prompt" style={{ marginTop: '16px' }}>
              Don't have an account?{' '}
              <Link to="/signup" className="footer-link">
                Sign up
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailOTPLogin;
